// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { IlliquidAssetsState } from '@shared/data-access/interfaces/redux/illiquid-assets-state-types';
import type { IlliquidAsset } from '@shared/data-access/types/engine-types';
import {
  illiquidAssetsClientAdapter,
  illiquidAssetsDataAdapter,
  initialState,
} from './illiquid-assets-state';

export const illiquidAssetsSlice = createSlice({
  name: 'illiquidAssets',
  initialState: () => initialState,
  reducers: {
    reset: (): IlliquidAssetsState => initialState,

    setClientIlliquidAssets: (
      state,
      { payload }: PayloadAction<{ id: number; assets: IlliquidAsset[] }>,
    ) => {
      illiquidAssetsClientAdapter.upsertOne(state.assets, {
        id: payload.id,
        data: illiquidAssetsDataAdapter.setAll(
          illiquidAssetsDataAdapter.getInitialState(),
          payload.assets,
        ),
      });
    },

    updateIlliquidAsset: (
      state,
      { payload }: PayloadAction<{ id: number; asset: IlliquidAsset }>,
    ) => {
      const client = illiquidAssetsClientAdapter
        .getSelectors()
        .selectById(state.assets, payload.id);

      if (!client) return;

      illiquidAssetsClientAdapter.updateOne(state.assets, {
        id: payload.id,
        changes: {
          data: illiquidAssetsDataAdapter.updateOne(client.data, {
            id: payload.asset.id,
            changes: payload.asset,
          }),
        },
      });
    },

    addIlliquidAsset: (
      state,
      {
        payload,
      }: PayloadAction<{
        id: number;
        asset: IlliquidAsset;
      }>,
    ) => {
      const client = illiquidAssetsClientAdapter
        .getSelectors()
        .selectById(state.assets, payload.id);

      illiquidAssetsClientAdapter.upsertOne(state.assets, {
        id: payload.id,
        data: illiquidAssetsDataAdapter.addOne(
          client?.data ?? illiquidAssetsDataAdapter.getInitialState(),
          payload.asset,
        ),
      });
    },

    removeIlliquidAssets: (
      state,
      {
        payload,
      }: PayloadAction<{
        id: number;
        assetId: number;
      }>,
    ) => {
      const client = illiquidAssetsClientAdapter
        .getSelectors()
        .selectById(state.assets, payload.id);

      if (!client) return;

      illiquidAssetsClientAdapter.updateOne(state.assets, {
        id: payload.id,
        changes: {
          data: illiquidAssetsDataAdapter.removeOne(client.data, payload.assetId),
        },
      });
    },

    removeIlliquidAssetEvent: (
      state,
      {
        payload,
      }: PayloadAction<{
        id: number;
        assetId: number;
        eventId: number;
      }>,
    ) => {
      const client = illiquidAssetsClientAdapter
        .getSelectors()
        .selectById(state.assets, payload.id);

      if (!client) return;

      const asset = illiquidAssetsDataAdapter
        .getSelectors()
        .selectById(client.data, payload.assetId);

      if (!asset?.events?.length) return;

      illiquidAssetsClientAdapter.updateOne(state.assets, {
        id: payload.id,
        changes: {
          data: illiquidAssetsDataAdapter.updateOne(client.data, {
            id: payload.assetId,
            changes: {
              events: asset.events.filter(event => event.id !== payload.eventId),
            },
          }),
        },
      });
    },
  },
});
